import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Agriculture as FarmIcon,
  Cloud as WeatherIcon,
  LocalShipping as OrderIcon,
  TrendingUp as PerformanceIcon,
  Warning as AlertIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Loyalty as LoyaltyIcon,
  AccountBalance as AccountIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import customerCRMAPI, { BridgeClubIntelligence, GeneralIntelligence } from '../../services/customerCrmAPI';

interface PlannerContextData {
  customer_id: number;
  customer_name: string;
  active_batches: {
    id: string;
    batch_number: string;
    profile_name: string;
    quantity: number;
    arrival_date: string;
    current_week: number | null;
    expected_harvest: string | null;
    days_to_harvest: number | null;
    status: string;
  }[];
  weather: {
    forecast: any | null;
    active_alerts: {
      id: number;
      alert_type: string;
      severity: string;
      start_date: string;
      end_date: string | null;
      description: string;
    }[];
    has_alerts: boolean;
  };
  upcoming_orders: {
    id: string;
    ship_date: string;
    status: string;
    status_display: string;
    verification_deadline: string;
    is_overdue: boolean;
    days_until_ship: number;
  }[];
  recent_performance: {
    batch_number: string;
    harvest_date: string | null;
    fcr: number | null;
    mortality_percent: number | null;
    avg_live_weight: number | null;
  }[];
  summary: {
    active_batch_count: number;
    pending_orders: number;
    alert_count: number;
  };
}

interface CustomerPlannerContextProps {
  customerId: number;
  customerType: string;
}

// ─── Farm Intelligence Panel (WHOLESALE_FARM) ───────────────────────────────

const FarmIntelligencePanel: React.FC<{ customerId: number }> = ({ customerId }) => {
  const [data, setData] = useState<PlannerContextData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/planner/crm/customers/${customerId}/planner-context/`);
        setData(response.data);
      } catch (err: any) {
        console.error('Failed to load planner context:', err);
        if (err.response?.status === 404) {
          setData(null);
        } else {
          setError('Failed to load farm intelligence data');
        }
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  if (!loading && !data && !error) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'success';
      case 'pending_verification': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getSummaryText = () => {
    if (!data) return '';
    const parts = [];
    if (data.summary.active_batch_count > 0) {
      parts.push(`${data.summary.active_batch_count} active batch${data.summary.active_batch_count > 1 ? 'es' : ''}`);
    }
    if (data.summary.pending_orders > 0) {
      parts.push(`${data.summary.pending_orders} pending order${data.summary.pending_orders > 1 ? 's' : ''}`);
    }
    if (data.summary.alert_count > 0) {
      parts.push(`${data.summary.alert_count} weather alert${data.summary.alert_count > 1 ? 's' : ''}`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'No active planner data';
  };

  return (
    <Paper
      sx={{
        mb: 3,
        border: data?.weather?.has_alerts ? '2px solid #f57c00' : '1px solid #e0e0e0',
        bgcolor: data?.weather?.has_alerts ? '#fff3e0' : '#fafafa',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <FarmIcon sx={{ mr: 1.5, color: '#2e7d32' }} />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#2e7d32' }}>
          Farm Intelligence
        </Typography>

        {data?.weather?.has_alerts && (
          <Chip
            icon={<AlertIcon sx={{ fontSize: 16 }} />}
            label={`${data.summary.alert_count} Alert${data.summary.alert_count > 1 ? 's' : ''}`}
            size="small"
            sx={{
              ml: 2,
              bgcolor: '#f57c00',
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        )}

        {!expanded && data && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {getSummaryText()}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {loading && <LinearProgress />}

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Collapse in={expanded && !loading && !!data}>
        {data && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {data.weather.active_alerts.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {data.weather.active_alerts.map((alert) => (
                  <Alert
                    key={alert.id}
                    severity={alert.severity === 'extreme' ? 'error' : alert.severity === 'severe' ? 'warning' : 'info'}
                    icon={<WeatherIcon />}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {alert.alert_type.replace('_', ' ').toUpperCase()} - {alert.severity.toUpperCase()}
                    </Typography>
                    <Typography variant="body2">
                      {alert.description}
                      {alert.end_date && ` (until ${formatDate(alert.end_date)})`}
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* Active Batches */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FarmIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Active Batches ({data.active_batches.length})
                  </Typography>
                </Box>
                {data.active_batches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No active batches
                  </Typography>
                ) : (
                  data.active_batches.slice(0, 3).map((batch) => (
                    <Box
                      key={batch.id}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {batch.profile_name}
                        </Typography>
                        <Chip
                          label={`Week ${batch.current_week || '?'}`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {batch.quantity.toLocaleString()} birds • {batch.batch_number}
                      </Typography>
                      {batch.days_to_harvest !== null && (
                        <Typography variant="caption" display="block" color={batch.days_to_harvest <= 7 ? 'warning.main' : 'text.secondary'}>
                          {batch.days_to_harvest > 0
                            ? `Harvest in ${batch.days_to_harvest} days`
                            : batch.days_to_harvest === 0
                              ? 'Harvest TODAY'
                              : `${Math.abs(batch.days_to_harvest)} days past expected harvest`}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>

              {/* Upcoming Orders */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <OrderIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Upcoming Orders ({data.upcoming_orders.length})
                  </Typography>
                </Box>
                {data.upcoming_orders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No upcoming orders
                  </Typography>
                ) : (
                  data.upcoming_orders.slice(0, 3).map((order) => (
                    <Box
                      key={order.id}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: order.is_overdue ? '#ffebee' : 'white',
                        borderRadius: 1,
                        border: order.is_overdue ? '1px solid #f44336' : '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          Ship: {formatDate(order.ship_date)}
                        </Typography>
                        <Chip
                          label={order.is_overdue ? 'OVERDUE' : order.status_display}
                          size="small"
                          color={order.is_overdue ? 'error' : getStatusColor(order.status) as any}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {order.days_until_ship > 0
                          ? `${order.days_until_ship} days until ship`
                          : order.days_until_ship === 0
                            ? 'Ships TODAY'
                            : `${Math.abs(order.days_until_ship)} days overdue`}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {/* Recent Performance */}
            {data.recent_performance.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PerformanceIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Recent Performance
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                  }}
                >
                  {data.recent_performance.map((perf) => (
                    <Box
                      key={perf.batch_number}
                      sx={{
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        minWidth: 150,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block">
                        {perf.batch_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {perf.harvest_date ? formatDate(perf.harvest_date) : 'N/A'}
                      </Typography>
                      {perf.fcr && (
                        <Typography variant="body2">
                          FCR: <strong>{perf.fcr.toFixed(2)}</strong>
                        </Typography>
                      )}
                      {perf.avg_live_weight && (
                        <Typography variant="body2">
                          Avg: <strong>{perf.avg_live_weight.toFixed(1)} lbs</strong>
                        </Typography>
                      )}
                      {perf.mortality_percent !== null && (
                        <Typography variant="body2" color={perf.mortality_percent > 5 ? 'error.main' : 'text.primary'}>
                          Mort: <strong>{perf.mortality_percent.toFixed(1)}%</strong>
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

// ─── Bridge Club Intelligence Panel (RETAIL_PARTNER) ────────────────────────

const BridgeClubIntelligencePanel: React.FC<{ customerId: number }> = ({ customerId }) => {
  const [data, setData] = useState<BridgeClubIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await customerCRMAPI.getBridgeClubIntelligence(customerId);
        setData(response.data);
      } catch (err: any) {
        console.error('Failed to load bridge club intelligence:', err);
        if (err.response?.status === 404) {
          setData(null);
        } else {
          setError('Failed to load Bridge Club intelligence');
        }
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  if (!loading && !data && !error) {
    return null;
  }

  const getSummaryText = () => {
    if (!data) return '';
    const parts = [];
    if (data.members.total > 0) parts.push(`${data.members.total} members`);
    if (data.purchases_last_90_days.count > 0) parts.push(`${data.purchases_last_90_days.count} purchases (90d)`);
    if (data.loyalty.pending > 0) parts.push(`${data.loyalty.pending} pending rewards`);
    return parts.length > 0 ? parts.join(' • ') : 'No Bridge Club data';
  };

  return (
    <Paper
      sx={{
        mb: 3,
        border: data?.loyalty?.pending ? '2px solid #2e7d32' : '1px solid #e0e0e0',
        bgcolor: '#f1f8e9',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <StoreIcon sx={{ mr: 1.5, color: '#2e7d32' }} />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#2e7d32' }}>
          Bridge Club Intelligence
        </Typography>

        {data?.loyalty?.pending ? (
          <Chip
            icon={<LoyaltyIcon sx={{ fontSize: 16 }} />}
            label={`${data.loyalty.pending} Pending Reward${data.loyalty.pending > 1 ? 's' : ''}`}
            size="small"
            sx={{
              ml: 2,
              bgcolor: '#2e7d32',
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        ) : null}

        {!expanded && data && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {getSummaryText()}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {loading && <LinearProgress color="success" />}

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Collapse in={expanded && !loading && !!data}>
        {data && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* Members */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Members
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Members</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.members.total}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Active (60 days)</Typography>
                    <Typography variant="body2" fontWeight="bold" color={data.members.active_last_60_days > 0 ? 'success.main' : 'text.secondary'}>
                      {data.members.active_last_60_days}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Purchases */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ShoppingCartIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Purchases (90 days)
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Transactions</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.purchases_last_90_days.count}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Bags Sold</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.purchases_last_90_days.total_bags}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Revenue</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${data.purchases_last_90_days.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Loyalty */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LoyaltyIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Loyalty Rewards
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Pending Redemptions</Typography>
                    <Chip
                      label={data.loyalty.pending}
                      size="small"
                      color={data.loyalty.pending > 0 ? 'warning' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Completed</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.loyalty.completed}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Analytics */}
              {data.analytics && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PerformanceIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Analytics
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    {data.analytics.top_products.length > 0 && (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Top Products</Typography>
                        {data.analytics.top_products.slice(0, 3).map((product, idx) => (
                          <Typography key={idx} variant="body2">{product}</Typography>
                        ))}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Period: {data.analytics.period}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

// ─── General Intelligence Panel (LOCAL_WILL_CALL / OTHER / CJ_CUSTOMER) ─────

const GeneralIntelligencePanel: React.FC<{ customerId: number }> = ({ customerId }) => {
  const [data, setData] = useState<GeneralIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await customerCRMAPI.getGeneralIntelligence(customerId);
        setData(response.data);
      } catch (err: any) {
        console.error('Failed to load general intelligence:', err);
        if (err.response?.status === 404) {
          setData(null);
        } else {
          setError('Failed to load intelligence data');
        }
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  if (!loading && !data && !error) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getSummaryText = () => {
    if (!data) return '';
    const parts = [];
    if (data.orders.total_count > 0) parts.push(`${data.orders.total_count} orders`);
    if (data.interactions.total > 0) parts.push(`${data.interactions.total} interactions`);
    if (data.account_health.days_since_contact !== null) {
      parts.push(`${data.account_health.days_since_contact}d since contact`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'No activity data';
  };

  const getUtilizationColor = (pct: number | null) => {
    if (pct === null) return 'text.secondary';
    if (pct >= 90) return 'error.main';
    if (pct >= 70) return 'warning.main';
    return 'success.main';
  };

  return (
    <Paper
      sx={{
        mb: 3,
        border: '1px solid #e0e0e0',
        bgcolor: '#e3f2fd',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <AssessmentIcon sx={{ mr: 1.5, color: '#1565c0' }} />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1565c0' }}>
          Customer Intelligence
        </Typography>

        {data && data.account_health?.days_since_contact != null && data.account_health.days_since_contact > 30 && (
          <Chip
            label={`${data.account_health.days_since_contact}d no contact`}
            size="small"
            sx={{
              ml: 2,
              bgcolor: '#f57c00',
              color: 'white',
            }}
          />
        )}

        {!expanded && data && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {getSummaryText()}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {loading && <LinearProgress />}

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Collapse in={expanded && !loading && !!data}>
        {data && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* Orders */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ShoppingCartIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Order History
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Orders</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.orders.total_count}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Total Revenue</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${data.orders.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Last Order</Typography>
                    <Typography variant="body2">{data.orders.last_order_date ? formatDate(data.orders.last_order_date) : 'Never'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Recent (90d)</Typography>
                    <Typography variant="body2" fontWeight="bold">{data.orders.recent_90_day_count}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Account Health */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Account Health
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Days Since Contact</Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={data.account_health.days_since_contact !== null && data.account_health.days_since_contact > 30 ? 'warning.main' : 'text.primary'}
                    >
                      {data.account_health.days_since_contact ?? 'N/A'}
                    </Typography>
                  </Box>
                  {data.account_health.next_follow_up && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">Follow Up</Typography>
                      <Typography variant="body2">{formatDate(data.account_health.next_follow_up)}</Typography>
                    </Box>
                  )}
                  {data.account_health.credit_limit !== null && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="body2">Credit Limit</Typography>
                        <Typography variant="body2">${data.account_health.credit_limit.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="body2">Balance</Typography>
                        <Typography variant="body2">${data.account_health.current_balance.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="body2">Utilization</Typography>
                        <Typography variant="body2" fontWeight="bold" color={getUtilizationColor(data.account_health.utilization_percent)}>
                          {data.account_health.utilization_percent !== null ? `${data.account_health.utilization_percent}%` : 'N/A'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* Recent Interactions */}
              {data.interactions.recent.length > 0 && (
                <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Recent Interactions ({data.interactions.total} total)
                    </Typography>
                  </Box>
                  {data.interactions.recent.map((interaction, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {interaction.type} - {interaction.caller}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(interaction.date)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {interaction.notes_preview}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {data.preferred_products && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Preferred Products: {data.preferred_products}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

// ─── Main Dispatcher ────────────────────────────────────────────────────────

const CustomerPlannerContext: React.FC<CustomerPlannerContextProps> = ({ customerId, customerType }) => {
  if (customerType === 'WHOLESALE_FARM') {
    return <FarmIntelligencePanel customerId={customerId} />;
  }

  if (customerType === 'RETAIL_PARTNER') {
    return <BridgeClubIntelligencePanel customerId={customerId} />;
  }

  // LOCAL_WILL_CALL, OTHER, CJ_CUSTOMER, and any other types
  return <GeneralIntelligencePanel customerId={customerId} />;
};

export default CustomerPlannerContext;
